// The one canvas behind every route. Routes declare a Scene with
// useWorldScene(); the stage frames its room, takes the lift between rooms
// (lift.ts: doors close, the dial swings, doors open on the next room), pans
// within one, and keeps in-world DOM anchors
// (nameplates, emotes, plaques) glued to the art. The canvas is decorative
// (aria-hidden); everything that matters is also said in the DOM.
import {
  createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type ReactNode,
} from 'react';
import { loadMeta, meta, onAssetLoad, canLoadImages, prefetchBuilding } from './assets';
import {
  planMove, worldToScreen, type CamState, type MovePlan, type Shot, type Viewport,
} from './camera';
import { doorsOpen, drawLift, liftTimeline, needleAt, prepareLift, resumeAt, tripBetween } from './lift';
import { drawWorld } from './renderer';
import { ROOM_LABELS, roomShot } from './rooms';
import type { Scene } from './scene';
import { prefersReducedMotion, REDUCED_MOTION_QUERY } from '../lib/motion';
import { useTheme } from '../lib/theme';
import { motionOverride, usePrefs } from './prefs';
import { SFX } from './sfx';

interface Anchor { el: HTMLElement; x: number; y: number }

export interface StageApi {
  setScene(owner: symbol, scene: Scene | null, priority?: number): void;
  /** Open the building map (a plan drawn over the room; the camera stays where it is), or close it. */
  setMapOpen(open: boolean): void;
  /** CSS px per art px right now (for sizing in-world DOM). */
  wp(): number;
  registerAnchor(el: HTMLElement, x: number, y: number): () => void;
  /** Current floor (for the map). */
  floor(): string | null;
  /** The room the camera is framing (for the map's you-are-here). */
  room(): string | null;
}

/** When the rest of the building starts downloading, after the world map. */
const PREFETCH_AFTER_MS = 800;

const StageCtx = createContext<StageApi | null>(null);
/** Changes rarely (start and end of a move, map open and shut), kept apart so the API object stays stable. */
export interface StageStatus { moving: boolean; mapOpen: boolean }
const StatusCtx = createContext<StageStatus>({ moving: false, mapOpen: false });

export function useStage(): StageApi | null {
  return useContext(StageCtx);
}

export function useStageStatus(): StageStatus {
  return useContext(StatusCtx);
}

function viewport(): Viewport {
  if (typeof window === 'undefined') return { w: 1280, h: 720, dpr: 1 };
  return { w: window.innerWidth, h: window.innerHeight, dpr: Math.max(1, Math.min(3, window.devicePixelRatio || 1)) };
}

function reduced(): boolean {
  const o = motionOverride();
  return o === null ? prefersReducedMotion() : o;
}

function camFromShot(s: Shot): CamState {
  return { floor: s.floor, cx: s.cx, cy: s.cy, scale: s.scale, lit: s.lit, dim: s.dim, ink: 0 };
}

/** While the lift runs, <html data-lift="room"> hides the HUD and the footer over the doors (kit.css), and tells tests. */
function liftFlag(room: string | null): void {
  if (typeof document === 'undefined') return;
  if (room) document.documentElement.dataset.lift = room;
  else delete document.documentElement.dataset.lift;
}

interface Move {
  plan: MovePlan;
  start: number;
  target: Shot;
  /** The shot the move left from (a lift holds on it until its doors shut). */
  from: Shot;
  /** The room the move goes to. */
  room: string;
  dinged: boolean;
}

export function WorldStage({ children }: { children: ReactNode }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const layerRef = useRef<HTMLDivElement | null>(null);
  const scenes = useRef<Array<{ owner: symbol; scene: Scene | null; priority: number }>>([]);
  const cam = useRef<CamState | null>(null);
  const move = useRef<Move | null>(null);
  /** The room the camera frames right now (during a lift, the one behind the doors). */
  const camRoom = useRef<string | null>(null);
  const anchors = useRef(new Set<Anchor>());
  const vpRef = useRef<Viewport>(viewport());
  const raf = useRef<number | null>(null);
  const lastIdle = useRef(0);
  const idleTimer = useRef<number | null>(null);
  const [moving, setMoving] = useState(false);
  const [mapOpen, setMapOpenState] = useState(false);
  const theme = useTheme();
  const night = theme === 'dark';
  const nightRef = useRef(night);
  nightRef.current = night;
  const enabled = canLoadImages();

  /** Highest priority wins; among equals, the latest declared. */
  const current = (): Scene | null => {
    let best: { scene: Scene | null; priority: number } | null = null;
    for (const e of scenes.current) if (e.scene && (!best || e.priority >= best.priority)) best = e;
    return best?.scene ?? null;
  };

  if (import.meta.env.DEV && typeof window !== 'undefined') {
    (window as unknown as { __world: unknown }).__world = { cam: () => cam.current, scene: () => current(), vp: () => vpRef.current, move: () => move.current, scenes: () => scenes.current.map((e) => [e.priority, e.scene?.room, JSON.stringify(e.scene?.center)]) };
  }

  const placeAnchors = useCallback(() => {
    const c = cam.current;
    const vp = vpRef.current;
    const layer = layerRef.current;
    if (!c) return;
    const wp = c.scale / vp.dpr;
    if (layer) layer.style.setProperty('--wp', `${wp}px`);
    // The camera, for CSS that places fixed UI in world coordinates:
    // left: calc(50vw + (X - var(--camx)) * var(--wp)).
    const root = document.documentElement.style;
    root.setProperty('--wp', `${wp}px`);
    root.setProperty('--camx', String(c.cx));
    root.setProperty('--camy', String(c.cy));
    // The largest whole-pixel size of the pixel face that fits one art pixel's worth of world text.
    root.setProperty('--fpx', `${Math.max(16, Math.floor((wp * 8) / 16) * 16)}px`);
    // A whole art pixel, never less than one CSS px: display type sized in art pixels multiplies this, so a fractional
    // device scale cannot put the pixel face at a fractional size.
    root.setProperty('--wpi', `${Math.max(1, Math.floor(wp))}px`);
    anchors.current.forEach((a) => {
      const [sx, sy] = worldToScreen(c, a.x, a.y, vp);
      // whole DEVICE pixels, so at DPR 3 a label cannot sit up to a pixel off the art it belongs to
      a.el.style.transform = `translate3d(${Math.round(sx * vp.dpr) / vp.dpr}px, ${Math.round(sy * vp.dpr) / vp.dpr}px, 0)`;
      a.el.style.setProperty('--wp', `${wp}px`);
    });
  }, []);

  const draw = useCallback((now: number) => {
    const canvas = canvasRef.current;
    const c = cam.current;
    if (!canvas || !c) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const sc = current();
    drawWorld(ctx, { cam: c, vp: vpRef.current, scene: sc, now, idle: !reducedRef.current, night: nightRef.current,
      skipRooms: sc?.replaceCast ? new Set([sc.room]) : undefined, castOverride: sc?.castOverride });
    const mv = move.current;
    if (mv?.plan.lift) drawLift(ctx, mv.plan.lift, now - mv.start, vpRef.current, nightRef.current);
    placeAnchors();
  }, [placeAnchors]);

  const reducedRef = useRef(reduced());
  /** The building is never still (M10): any floor with people or moving things in it keeps a calm frame rate. */
  const floorLives = (): boolean => {
    const m = meta();
    const f = cam.current?.floor;
    if (!m || !f) return false;
    return (m.cast ?? []).some((e) => e.floor === f) || (m.fx ?? []).some((e) => e.floor === f);
  };
  const needsIdle = (): boolean => {
    if (reducedRef.current) return false;
    const s = current();
    return !!s?.actors?.length || floorLives();
  };
  /** Talking mouths and walks need about 12 frames a second; the rest of the room's life is calm at 10. */
  const idleEvery = (): number => {
    const s = current();
    const now = typeof performance !== 'undefined' ? performance.now() : 0;
    const busy = s?.actors?.some((a) => a.talking || (a.kind === 'stand' && (a.walk || (a.tween && now < a.tween.t0 + a.tween.dur))));
    return busy ? 80 : 100;
  };

  const tick = useCallback((now: number) => {
    raf.current = null;
    const idleOn = needsIdle();
    const m = move.current;
    if (m) {
      const t = now - m.start;
      const done = t >= m.plan.duration;
      if (m.plan.lift) {
        const tl = liftTimeline(m.plan.lift);
        if (done || t >= tl.swap) camRoom.current = m.room;
        // The camera is already on the new room once the doors start to part, so
        // its papers can come up with them instead of after the ride.
        if (!done && t >= tl.open[0] && t >= tl.swap) setMoving(false);
        // The ding as the lamp lights; a skipped trip still arrives with it.
        if (!m.dinged && (done || t >= tl.ding)) { m.dinged = true; SFX.ding(); }
      }
      if (done) {
        cam.current = camFromShot(m.target);
        camRoom.current = m.room;
        move.current = null;
        setMoving(false);
        liftFlag(null);
      } else {
        cam.current = m.plan.at(t);
      }
      draw(now);
    } else if (idleOn && now - lastIdle.current >= idleEvery() - 20) {
      lastIdle.current = now;
      draw(now);
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    if (move.current) {
      raf.current = requestAnimationFrame(tick);
    } else if (idleOn && idleTimer.current === null) {
      // Between idle frames (blinks, mouths, walks) the page sleeps on a timer instead of waking every frame. The
      // frame it wakes into is stamped at its start, up to a frame early, hence the 20 ms of slack above.
      const wait = Math.max(0, idleEvery() - (now - lastIdle.current));
      idleTimer.current = window.setTimeout(() => {
        idleTimer.current = null;
        if (raf.current === null) raf.current = requestAnimationFrame(tick);
      }, wait);
    }
  }, [draw]);

  useEffect(() => () => {
    liftFlag(null);
    if (idleTimer.current !== null) window.clearTimeout(idleTimer.current);
    if (raf.current !== null && typeof cancelAnimationFrame === 'function') cancelAnimationFrame(raf.current);
    idleTimer.current = null;
    raf.current = null;
  }, []);

  const kick = useCallback(() => {
    if (raf.current === null && typeof requestAnimationFrame === 'function') raf.current = requestAnimationFrame(tick);
  }, [tick]);

  const redraw = useCallback(() => {
    draw(typeof performance !== 'undefined' ? performance.now() : 0);
    kick();
  }, [draw, kick]);

  /** Frame the current scene: cut on first show, otherwise plan a move. */
  const reframe = useCallback((animate: boolean) => {
    const m = meta();
    const s = current();
    if (!m) return;
    const vp = vpRef.current;
    const target = s ? roomShot(m, s, vp) : null;
    if (!target || !s) return;
    const from = cam.current;
    let running = move.current;
    // A resize (or a phone's toolbar sliding) during a lift keeps the lift; only where it lands is re-measured.
    if (!animate && running?.plan.lift && running.room === s.room) {
      const plan = planMove(running.from, target, { sameRoom: false, reduced: running.plan.lift.reduced, trip: running.plan.lift });
      move.current = { ...running, plan, target };
      redraw();
      return;
    }
    if (!from || !animate) {
      cam.current = camFromShot(target);
      camRoom.current = s.room;
      move.current = null;
      setMoving(false);
      liftFlag(null);
      redraw();
      return;
    }
    const now = performance.now();
    // A lift already going to this room only learns where in the room to land (a route's own scene arriving after the
    // route's room): same doors, same clock.
    if (running?.plan.lift && running.room === s.room) {
      const plan = planMove(running.from, target, { sameRoom: false, reduced: running.plan.lift.reduced, trip: running.plan.lift });
      move.current = { ...running, plan, target };
      kick();
      return;
    }
    // Back to the room you are still in before the doors have begun to close (two scene changes in one frame): no trip.
    if (running?.plan.lift && s.room === camRoom.current
      && doorsOpen(liftTimeline(running.plan.lift), now - running.start) >= 1 && now - running.start < liftTimeline(running.plan.lift).swap) {
      move.current = null;
      running = null;
      setMoving(false);
      liftFlag(null);
    }
    const fromShot: Shot = { floor: from.floor, cx: from.cx, cy: from.cy, scale: from.scale, lit: from.lit, dim: from.dim };
    const same = !running && camRoom.current === s.room && fromShot.floor === target.floor && fromShot.cx === target.cx
      && fromShot.cy === target.cy && fromShot.scale === target.scale && fromShot.dim === target.dim
      && JSON.stringify(fromShot.lit) === JSON.stringify(target.lit);
    if (same) { redraw(); return; }
    const isReduced = reduced();
    // Inside one room the camera pans; anything else is the lift, and a second move during a lift keeps its doors.
    const sameRoom = !running?.plan.lift && camRoom.current === s.room;
    const trip = sameRoom ? undefined : tripBetween(from.floor, target.floor, ROOM_LABELS[s.room] ?? s.room, isReduced);
    let start = now;
    let dinged = false;
    if (running?.plan.lift && trip && !trip.reduced) {
      // Redirected: the doors stay as they stand, the needle swings on from where it points, and a car that has
      // already rung for this floor does not ring again.
      const old = running.plan.lift;
      const t = now - running.start;
      trip.fromPos = needleAt(old, t);
      start = now - resumeAt(liftTimeline(trip), doorsOpen(liftTimeline(old), t));
      dinged = running.dinged && old.to === trip.to;
    } else if (trip && !trip.reduced) {
      SFX.liftDoor();
    }
    if (trip?.reduced && !running?.dinged) SFX.ding();
    if (trip?.reduced) dinged = true;
    if (trip) prepareLift(trip, vpRef.current, nightRef.current);
    const plan = planMove(fromShot, target, { sameRoom, reduced: isReduced, trip });
    move.current = { plan, start, target, from: fromShot, room: s.room, dinged };
    // A pan inside one room (a phone following the speaker) never hides the route's UI; only a move between rooms does.
    setMoving(plan.kind !== 'pan');
    liftFlag(plan.lift ? s.room : null);
    kick();
  }, [kick, redraw]);

  const api = useMemo<StageApi>(() => ({
    setScene(owner, scene, priority = 1) {
      const list = scenes.current;
      const prev = current();
      const i = list.findIndex((e) => e.owner === owner);
      if (scene === null) { if (i >= 0) list.splice(i, 1); }
      else if (i >= 0) { list[i].scene = scene; list[i].priority = priority; }
      else list.push({ owner, scene, priority });
      const next = current();
      if (prev === next) return;
      scene = next;
      const roomChanged = (prev?.room ?? null) !== (scene?.room ?? null)
        || JSON.stringify(prev?.center) !== JSON.stringify(scene?.center)
        || JSON.stringify(prev?.lit) !== JSON.stringify(scene?.lit)
        || prev?.dim !== scene?.dim
        || JSON.stringify(prev?.focus) !== JSON.stringify(scene?.focus);
      if (roomChanged) reframe(cam.current !== null);
      else redraw();
    },
    setMapOpen(open) {
      setMapOpenState(open);
    },
    wp() { return cam.current ? cam.current.scale / vpRef.current.dpr : 1; },
    registerAnchor(el, x, y) {
      const a = { el, x, y };
      anchors.current.add(a);
      placeAnchors();
      return () => { anchors.current.delete(a); };
    },
    floor() { return cam.current?.floor ?? null; },
    room() { return current()?.room ?? null; },
  }), [reframe, redraw, placeAnchors]);
  const status = useMemo(() => ({ moving, mapOpen }), [moving, mapOpen]);

  // canvas size follows the viewport; a resize reframes without animation
  useLayoutEffect(() => {
    if (!enabled) return undefined;
    const fit = () => {
      vpRef.current = viewport();
      const c = canvasRef.current;
      const { w, h, dpr } = vpRef.current;
      if (c) {
        // Sized from the visible viewport (innerWidth/innerHeight), never 100vh, which on phones is the large
        // viewport and would stretch the bitmap off whole pixels.
        c.width = Math.round(w * dpr);
        c.height = Math.round(h * dpr);
        c.style.width = `${w}px`;
        c.style.height = `${h}px`;
      }
      // CSS that places UI in world coordinates uses the same viewport the camera does.
      document.documentElement.style.setProperty('--vw', `${w / 100}px`);
      document.documentElement.style.setProperty('--vh', `${h / 100}px`);
      reframe(false);
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [enabled, reframe]);

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    // A failed fetch is retried with backoff; the UI never waits on the world.
    let wait = 2000;
    let prefetch: number | null = null;
    const load = () => loadMeta().then(() => {
      if (!alive) return;
      reframe(false);
      // The rest of the building once this room has had the line to itself.
      prefetch = window.setTimeout(prefetchBuilding, PREFETCH_AFTER_MS);
    }).catch(() => {
      if (alive) window.setTimeout(load, wait = Math.min(wait * 2, 30000));
    });
    void load();
    const off = onAssetLoad(() => redraw());
    const vis = () => { if (document.visibilityState === 'visible') redraw(); };
    document.addEventListener('visibilitychange', vis);
    return () => {
      alive = false; off(); document.removeEventListener('visibilitychange', vis);
      if (prefetch !== null) window.clearTimeout(prefetch);
    };
  }, [enabled, reframe, redraw]);

  // any input during a move lands it immediately
  useEffect(() => {
    if (!moving) return undefined;
    const skip = (e: Event) => {
      // A key held down from before the move repeats; only a fresh press skips.
      if (e instanceof KeyboardEvent && (e.repeat || e.key === 'Tab' || e.key === 'Shift')) return;
      const mv = move.current;
      if (mv) mv.start = -1e9;
    };
    window.addEventListener('pointerdown', skip, true);
    window.addEventListener('keydown', skip, true);
    return () => {
      window.removeEventListener('pointerdown', skip, true);
      window.removeEventListener('keydown', skip, true);
    };
  }, [moving]);

  useEffect(() => { redraw(); }, [night, redraw]);

  // Under reduced motion the frame is still, but the clocks keep telling the time.
  useEffect(() => {
    const id = window.setInterval(() => { if (reducedRef.current) redraw(); }, 30000);
    return () => window.clearInterval(id);
  }, [redraw]);

  // Reduced motion: the system setting or the player's override, re-read when either changes.
  const prefs = usePrefs();
  useEffect(() => {
    const mq = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(REDUCED_MOTION_QUERY) : null;
    const update = () => {
      reducedRef.current = reduced();
      const o = motionOverride();
      document.documentElement.dataset.motion = o === true ? 'reduce' : o === false ? 'full' : '';
      redraw();
    };
    update();
    mq?.addEventListener?.('change', update);
    return () => mq?.removeEventListener?.('change', update);
  }, [prefs.motion, redraw]);

  return (
    <StageCtx.Provider value={api}>
      <StatusCtx.Provider value={status}>
        {enabled && <canvas ref={canvasRef} className="world-canvas" aria-hidden="true" />}
        <div ref={layerRef} className="world-anchors" />
        {children}
      </StatusCtx.Provider>
    </StageCtx.Provider>
  );
}

/**
 * Declare what the world shows while this component is mounted. The latest
 * mounted declaration wins; pass null to hand the stage back.
 */
export function useWorldScene(scene: Scene | null, priority = 1): void {
  const stage = useStage();
  const owner = useRef(Symbol('scene'));
  const key = scene ? JSON.stringify(scene) : null;
  useEffect(() => {
    if (!stage) return undefined;
    stage.setScene(owner.current, scene, priority);
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, key, priority]);
  useEffect(() => {
    const o = owner.current;
    return () => { stage?.setScene(o, null); };
  }, [stage]);
}

/**
 * DOM glued to a point of the world (floor pixels). It is position: fixed and
 * moved by transform every frame the camera moves; `--wp` is one art pixel in
 * CSS px, for sizing things to match the art.
 */
export function WorldAnchor({ x, y, className, style, children, label }: {
  x: number; y: number; className?: string; style?: CSSProperties; children?: ReactNode; label?: string;
}) {
  const stage = useStage();
  const ref = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!stage || !el) return undefined;
    return stage.registerAnchor(el, x, y);
  }, [stage, x, y]);
  return (
    <div ref={ref} className={`world-anchor${className ? ` ${className}` : ''}`} style={style} aria-label={label}>
      {children}
    </div>
  );
}

/** A component that only declares a room (no people). */
export function RoomScene({ priority = 1, ...scene }: Scene & { priority?: number }) {
  useWorldScene(scene, priority);
  return null;
}
